import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import {
  Check, Crown, BookOpen, Loader2, ArrowRight,
  Mic, Brain, BarChart3, Shield, Sparkles, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_CONFIG = {
  student: {
    icon: BookOpen,
    gradient: 'from-blue-500 to-cyan-500',
    highlights: ['Unlimited lectures', 'HD transcription', 'Quiz Generator', '10GB storage'],
  },
  pro: {
    icon: Crown,
    gradient: 'from-purple-500 to-indigo-600',
    badge: 'Most Popular',
    highlights: ['Everything in Student', 'Multi-speaker detection', 'Custom AI prompts', 'Advanced Analytics'],
  },
};

export const UpgradeModal = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const { isPremium, plan } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');

  useEffect(() => {
    if (open) {
      axios.get(`${API}/payments/plans`)
        .then(res => setPlans(res.data.plans || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleSubscribe = async (planId) => {
    if (!user) return;
    setProcessingPlan(planId);
    try {
      const res = await axios.post(`${API}/payments/initialize`, {
        email: user.email,
        plan_id: planId,
        interval: billingCycle,
        currency: 'USD',
        channels: ['card', 'mobile_money', 'bank'],
      });
      if (res.data?.data?.authorization_url) {
        window.location.href = res.data.data.authorization_url;
      }
    } catch (err) {
      console.error('Payment error:', err);
    } finally {
      setProcessingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] p-0 overflow-hidden" data-testid="upgrade-modal">
        <DialogHeader className="p-6 pb-3">
          <DialogTitle className="text-2xl font-bold text-[var(--isp-heading)] font-heading flex items-center gap-2">
            <Crown className="w-6 h-6 text-[#FFD700]" /> Upgrade Your Plan
          </DialogTitle>
          <p className="text-sm text-[var(--isp-body)]" id="upgrade-modal-description">Unlock the full power of AI-driven study tools.</p>
        </DialogHeader>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 px-6 pb-2" data-testid="modal-billing-toggle">
          <span className={`text-xs font-medium ${billingCycle === 'monthly' ? 'text-[var(--isp-heading)]' : 'text-[var(--isp-muted)]'}`}>Monthly</span>
          <button
            onClick={() => setBillingCycle(b => b === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-purple-500' : 'bg-[var(--isp-glass-border)]'}`}
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
          <span className={`text-xs font-medium ${billingCycle === 'yearly' ? 'text-[var(--isp-heading)]' : 'text-[var(--isp-muted)]'}`}>
            Yearly <span className="text-green-400 font-semibold">Save 58%</span>
          </span>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 pt-2">
          {loading ? (
            <div className="col-span-2 flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : plans.map((planData) => {
            const config = PLAN_CONFIG[planData.id] || PLAN_CONFIG.student;
            const Icon = config.icon;
            const isCurrentPlan = plan === planData.id;
            const price = billingCycle === 'yearly' ? planData.yearly_price : planData.monthly_price;

            return (
              <div
                key={planData.id}
                className={`relative p-5 rounded-xl border transition-all ${
                  config.badge
                    ? 'border-purple-500/40 ring-1 ring-purple-500/20 bg-purple-500/5'
                    : 'border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)]'
                }`}
                data-testid={`modal-plan-${planData.id}`}
              >
                {config.badge && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[10px] font-bold">
                    {config.badge}
                  </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${config.gradient}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-[var(--isp-heading)] font-heading">{planData.name}</h3>
                </div>

                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-2xl font-bold text-[var(--isp-heading)]">{planData.symbol}{price.toFixed(2)}</span>
                  <span className="text-xs text-[var(--isp-muted)]">/mo</span>
                </div>

                <div className="space-y-2 mb-4">
                  {config.highlights.map((h, i) => (
                    <div key={`highlight-${i}`} className="flex items-center gap-2 text-xs text-[var(--isp-body)]">
                      <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                      {h}
                    </div>
                  ))}
                </div>

                {isCurrentPlan || isPremium ? (
                  <div className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium">
                    <Check className="w-3 h-3" /> {isCurrentPlan ? 'Current Plan' : 'Already Premium'}
                  </div>
                ) : (
                  <Button
                    onClick={() => handleSubscribe(planData.id)}
                    disabled={!!processingPlan}
                    size="sm"
                    className={`w-full rounded-lg gap-1.5 text-white font-semibold bg-gradient-to-r ${config.gradient} hover:opacity-90 text-xs`}
                    data-testid={`modal-subscribe-${planData.id}`}
                  >
                    {processingPlan === planData.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>Get {planData.name} <ArrowRight className="w-3 h-3" /></>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
